import { useCallback, useEffect, useRef, useState } from "react";
import socketManager from "../utils/socket";

/* ---------- format ---------- */
export const money = (n) =>
  (Number(n) || 0).toLocaleString("th-TH", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

export const money2 = (n) =>
  (Number(n) || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function fmtClock(ms) {
  if (ms <= 0) return { d: 0, h: 0, m: 0, s: 0, done: true };
  const s = Math.floor(ms / 1000);
  return {
    d: Math.floor(s / 86400),
    h: Math.floor((s % 86400) / 3600),
    m: Math.floor((s % 3600) / 60),
    s: s % 60,
    done: false,
  };
}

export const pad2 = (n) => String(n).padStart(2, "0");

const THAI_MONTHS_SHORT = [
  "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.",
  "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค.",
];

// round_label จากระบบเป็น "งวด DD/MM/YYYY" (พ.ศ.) อยู่แล้ว — แค่จัดรูปแบบใหม่ให้อ่านง่ายขึ้น
// เป็น "D เดือนย่อ YY" (เช่น "16 ก.ย. 69") โดยไม่ต้องคำนวณวันที่ใหม่ (เลี่ยงปัญหา timezone)
// ป้าย ยี่กี ("รอบ N") ไม่มีรูปแบบวันที่ให้จับคู่ เลยคืนค่าเดิมตรง ๆ
export function formatRoundDate(label) {
  if (!label) return "";
  const m = String(label).match(/(\d{2})\/(\d{2})\/(\d{4})/);
  if (!m) return label;
  const [, dd, mm, yyyy] = m;
  const month = THAI_MONTHS_SHORT[Number(mm) - 1] || mm;
  // หวยที่แอดมินสร้างเองออกได้หลายรอบต่อวัน → label ต่อท้ายด้วยเวลา ("งวด 25/10/2569 14:00") ต้องคงเวลาไว้แยกรอบ
  const time = String(label).match(/\b([01]\d|2[0-3]):[0-5]\d\b/);
  return `${Number(dd)} ${month} ${yyyy.slice(-2)}${time ? ` · ${time[0]}` : ""}`;
}

// วัน-เวลาที่ผลจะออก จาก result_at (เวลาไทยเสมอ ไม่ขึ้นกับ timezone เครื่อง) → { date: "30 ต.ค. 69", time: "21:30", day: "ศ.", isToday }
const THAI_DAYS_SHORT = ["อา.", "จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส."];
export function formatResultAt(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  const bkk = new Date(d.getTime() + 7 * 3600 * 1000); // อ่านด้วย getUTC* = เวลาไทย
  const nowBkk = new Date(Date.now() + 7 * 3600 * 1000);
  return {
    isToday: bkk.toISOString().slice(0, 10) === nowBkk.toISOString().slice(0, 10),
    date: `${bkk.getUTCDate()} ${THAI_MONTHS_SHORT[bkk.getUTCMonth()]} ${String(bkk.getUTCFullYear() + 543).slice(-2)}`,
    time: `${pad2(bkk.getUTCHours())}:${pad2(bkk.getUTCMinutes())}`,
    day: THAI_DAYS_SHORT[bkk.getUTCDay()],
  };
}

export function betTypeLabel(code) {
  return (
    {
      "3T": "3 ตัวบน",
      "3TOD": "3 ตัวโต๊ด",
      "3B": "3 ตัวล่าง",
      "3F": "3 ตัวหน้า",
      "2T": "2 ตัวบน",
      "2B": "2 ตัวล่าง",
      RUN_T: "วิ่งบน",
      RUN_B: "วิ่งล่าง",
      "19DOOR": "19 ประตู",
      RUD_F: "รูดหน้า",
      RUD_B: "รูดหลัง",
      REVERSE: "กลับเลข",
      "4D_BIG": "4D ใหญ่ (Big)",
      "4D_SMALL": "4D เล็ก (Small)",
    }[code] || code
  );
}

/** วิธีแทงที่ลูกค้าใช้ (lotto_bet_item.input_method) — แทงเลขตรง ๆ (DIRECT) คืนค่าว่าง ไม่ต้องแสดง */
export function inputMethodLabel(code) {
  return { "19DOOR": "19 ประตู", RUD_F: "รูดหน้า", RUD_B: "รูดหลัง", REVERSE: "กลับเลข" }[code] || "";
}

// ไอคอนสำรองชั้นสุดท้ายจริง ๆ (กรณีแอดมินเพิ่มหวยชนิดใหม่ที่ยังไม่มีธง/โลโก้ผูกไว้)
// คืนค่าไอคอน Iconify ตามหมวด ไม่ใช่ emoji
export const catIcon = (category) => {
  if (category === "STOCK") return "solar:chart-2-bold-duotone";
  if (category === "YEEKEE") return "solar:bolt-circle-bold-duotone";
  if (category === "GOV") return "solar:buildings-2-bold-duotone";
  if (category === "4D") return "solar:hashtag-square-bold-duotone";
  return "solar:ticket-bold-duotone";
};

/* ---------- ธงประเทศ (จริง ไม่ใช่ emoji) — ผูกกับ code ของแต่ละชนิดหวย/หุ้น ---------- */
// Emoji ธงเรนเดอร์ไม่เหมือนกันในทุก OS (เช่น Windows โชว์เป็นตัวอักษร ISO code แทนธงจริง)
// เลยใช้รูปธงจริงจาก flagcdn.com (บริการสาธารณะฟรี ให้ธง PNG/SVG ตาม ISO 3166-1 alpha-2)
const COUNTRY_ISO = {
  GOV_TH: "th",
  GSB: "th",
  BAAC: "th",
  STOCK_TH_EVE: "th",
  LAO_DEV: "la",
  LAO_VIP: "la",
  LAO_STAR: "la",
  MALAY: "my",
  S4D_NIKKEI_AM: "jp",
  S4D_NIKKEI_PM: "jp",
  S4D_CHINA_AM: "cn",
  S4D_CHINA_PM: "cn",
  S4D_HSI_AM: "hk",
  S4D_HSI_PM: "hk",
  S4D_TAIWAN: "tw",
  S4D_KOREA: "kr",
  S4D_SINGAPORE: "sg",
  S4D_UK: "gb",
  S4D_GERMANY: "de",
  S4D_RUSSIA: "ru",
  S4D_DOWJONES: "us",
  HANOI: "vn",
  HANOI_SPECIAL: "vn",
  HANOI_VIP: "vn",
  STOCK_DOWJONES: "us",
  STOCK_NIKKEI_AM: "jp",
  STOCK_NIKKEI_PM: "jp",
  STOCK_HSI_AM: "hk",
  STOCK_HSI_PM: "hk",
  STOCK_CHINA_AM: "cn",
  STOCK_CHINA_PM: "cn",
  STOCK_TAIWAN: "tw",
  STOCK_KOREA: "kr",
  STOCK_SINGAPORE: "sg",
  STOCK_EGYPT: "eg",
  STOCK_GERMANY: "de",
  STOCK_UK: "gb",
  STOCK_RUSSIA: "ru",
};

/* ---------- ไอคอนที่แอดมินอัปโหลดให้หวยที่สร้างเอง (lotto_type.flag_icon) ---------- */
// FlagBadge ถูกเรียกด้วย code ตามหลายหน้า → เก็บ map code → URL ไว้ที่ module เดียว
// ถูกเติมเมื่อ lottoApi.types() ตอบกลับ (หรือโหลดเองครั้งแรกที่มีป้ายขึ้น) แล้วแจ้ง FlagBadge ให้วาดใหม่
let customIcons = {};
let iconsLoaded = false;
const iconSubs = new Set();
export const typeIconsLoaded = () => iconsLoaded;
export function registerTypeIcons(list, base = "") {
  const next = {};
  (Array.isArray(list) ? list : []).forEach((t) => {
    if (t && t.flag_icon && t.code) next[t.code] = /^https?:\/\//i.test(t.flag_icon) ? t.flag_icon : `${base}${t.flag_icon}`;
  });
  const same = Object.keys(next).length === Object.keys(customIcons).length && Object.keys(next).every((k) => customIcons[k] === next[k]);
  customIcons = next;
  iconsLoaded = true;
  if (!same) iconSubs.forEach((fn) => fn());
}
export const customIconUrl = (code) => customIcons[String(code || "")] || null;
export const subscribeTypeIcons = (fn) => {
  iconSubs.add(fn);
  return () => iconSubs.delete(fn);
};

export function countryFlagUrl(code, width = 80) {
  const iso = COUNTRY_ISO[String(code || "")];
  return iso ? `https://flagcdn.com/w${width}/${iso}.png` : null;
}

/* ---------- ป้ายแยกหวยย่อยในประเทศเดียวกัน ---------- */
// บางประเทศมีหลายหวย/รอบย่อยใช้ธงเดียวกัน (เช่น ลาวพัฒนา/VIP/สตาร์, ฮานอยปกติ/พิเศษ/VIP,
// หุ้นเช้า/บ่าย) ธงอย่างเดียวแยกไม่ออก — ใส่ป้ายข้อความสั้น ๆ ทับมุมธงให้แยกชนิดได้ในสายตาแรก
const VARIANT_TAG = {
  GOV_TH: "รัฐ",
  GSB: "ออมสิน",
  BAAC: "ธกส",
  STOCK_TH_EVE: "เย็น",
  LAO_DEV: "พัฒนา",
  LAO_VIP: "VIP",
  LAO_STAR: "สตาร์",
  S4D_NIKKEI_AM: "เช้า",
  S4D_NIKKEI_PM: "บ่าย",
  S4D_CHINA_AM: "เช้า",
  S4D_CHINA_PM: "บ่าย",
  S4D_HSI_AM: "เช้า",
  S4D_HSI_PM: "บ่าย",
  HANOI: "ปกติ",
  HANOI_SPECIAL: "พิเศษ",
  HANOI_VIP: "VIP",
  STOCK_DOWJONES: "Dow",
  STOCK_NIKKEI_AM: "เช้า",
  STOCK_NIKKEI_PM: "บ่าย",
  STOCK_HSI_AM: "เช้า",
  STOCK_HSI_PM: "บ่าย",
  STOCK_CHINA_AM: "เช้า",
  STOCK_CHINA_PM: "บ่าย",
};

export function variantTag(code) {
  return VARIANT_TAG[String(code || "")] || null;
}

/** หวยหุ้น 4D (S4D_*) — ธงประเทศ + ตรา "4D" สีทอง */
export const isStock4D = (code) => String(code || "").startsWith("S4D_");

/** raw_value ของผล 4D ("m=LIVE;p=5422.16;pct=0.13;…") → object — ใช้โชว์ราคาที่นำมาออกผล */
export function parse4DSource(raw) {
  if (!raw || typeof raw !== "string") return null;
  const o = {};
  raw.split(";").forEach((pair) => {
    const i = pair.indexOf("=");
    if (i > 0) o[pair.slice(0, i)] = pair.slice(i + 1);
  });
  return o.m ? o : null;
}

/* ---------- ไอคอนสำรอง (ชนิดหวยที่ไม่ผูกกับประเทศ เช่น ยี่กี) ---------- */
// ใช้ไอคอนเวกเตอร์จาก Iconify (ชุด solar) แทน emoji เปล่า ๆ ให้คมชัด/สวยสม่ำเสมอทุกเครื่อง
// ไล่ระดับไอคอนตามความเร็วรอบ (15 นาที) ให้แยกแยะง่ายและดูมีมิติกว่าเดิม
const FALLBACK_ICON = {
  YEEKEE_15: "solar:hourglass-line-bold-duotone",
};

export function fallbackIcon(code) {
  return FALLBACK_ICON[String(code || "")] || null;
}

/* ---------- countdown hook ---------- */
export function useCountdown(targetIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const target = targetIso ? new Date(targetIso).getTime() : 0;
  return fmtClock(target - now);
}

/* ---------- toast hook (module-level bus) ---------- */
const listeners = new Set();
let seq = 0;
export function pushToast(message, kind = "ok", ttl = 3800) {
  const t = { id: ++seq, message, kind };
  listeners.forEach((fn) => fn({ type: "add", t }));
  setTimeout(() => listeners.forEach((fn) => fn({ type: "remove", id: t.id })), ttl);
}
export function useToasts() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const fn = (ev) => {
      if (ev.type === "add") setItems((x) => [...x, ev.t]);
      else setItems((x) => x.filter((i) => i.id !== ev.id));
    };
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);
  return items;
}

/* ---------- lotto socket hook ---------- */
export function useLottoSocket(userId, { onWallet, onRoundSettled, onRoundClosed } = {}) {
  const cb = useRef({});
  cb.current = { onWallet, onRoundSettled, onRoundClosed };

  useEffect(() => {
    const s = socketManager.connect();
    if (!s) return;
    const handlers = [];

    if (userId) {
      try {
        s.emit("join-user-room", userId);
      } catch {}
      const wEv = `wallet-${userId}`;
      const dEv = `deposit-${userId}`;
      const wh = (p) => cb.current.onWallet?.(p);
      s.on(wEv, wh);
      s.on(dEv, wh);
      handlers.push([wEv, wh], [dEv, wh]);
    }

    // round-settled-* / round-closed-* : subscribe wildcard via onAny
    const anyFn = (event, payload) => {
      if (event.startsWith("round-settled-")) cb.current.onRoundSettled?.(payload, event);
      else if (event.startsWith("round-closed-")) cb.current.onRoundClosed?.(payload, event);
    };
    s.onAny(anyFn);

    return () => {
      handlers.forEach(([ev, fn]) => s.off(ev, fn));
      s.offAny(anyFn);
    };
  }, [userId]);
}

/* ---------- attribution (แคมเปญ/พันธมิตร) ---------- */
const ATTR_KEY = "mkt_attr";
export function captureAttribution() {
  try {
    const u = new URL(window.location.href);
    const q = u.searchParams;
    const rf = q.get("rf") || q.get("registerFrom");
    const ref = q.get("ref");
    const has = rf || ref || q.get("utm_source");
    if (!has) return;
    const existing = getAttribution();
    const attr = {
      registerFrom: rf ? Number(rf) : existing.registerFrom || 0,
      ref: ref || existing.ref || null,
      clickId: q.get("cid") ? Number(q.get("cid")) : existing.clickId || null,
      utm_source: q.get("utm_source") || existing.utm_source || null,
      utm_medium: q.get("utm_medium") || existing.utm_medium || null,
      utm_campaign: q.get("utm_campaign") || existing.utm_campaign || null,
      utm_content: q.get("utm_content") || existing.utm_content || null,
      referrer: document.referrer || existing.referrer || null,
      landingPath: existing.landingPath || u.pathname,
      firstSeenAt: existing.firstSeenAt || new Date().toISOString(),
    };
    localStorage.setItem(ATTR_KEY, JSON.stringify(attr));
    // ล้าง param ออกจาก URL
    ["rf", "registerFrom", "ref", "cid", "utm_source", "utm_medium", "utm_campaign", "utm_content"].forEach(
      (k) => q.delete(k),
    );
    window.history.replaceState({}, "", u.pathname + (q.toString() ? "?" + q.toString() : ""));
  } catch {
    /* noop */
  }
}
export function getAttribution() {
  try {
    return JSON.parse(localStorage.getItem(ATTR_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

/* ---------- misc ---------- */
export function useInterval(fn, ms) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (ms == null) return;
    const id = setInterval(() => ref.current(), ms);
    return () => clearInterval(id);
  }, [ms]);
}

export function useAsync(fn, deps = []) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const run = useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    Promise.resolve()
      .then(fn)
      .then((data) => setState({ loading: false, data, error: null }))
      .catch((error) => setState({ loading: false, data: null, error }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(run, [run]);
  return { ...state, reload: run };
}
